import { cn } from 'util/cn'

import ahoy from 'ahoy.js'
import { MEDIA_QUERIES, css } from 'design'
import { Stack } from 'primitives/containers'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { useMediaControls } from '../../hooks/media/use_media_controls'
import usePreviousProps from '../../hooks/previous_props_hook'
import { useMediaQuery } from '../../hooks/use_media_query'
import {
  LoadingSpinner,
  MinimiseIcon,
  SoundOffIcon,
  SoundOnIcon,
} from '../../images'
import {
  COLLECTION_MUTED_EVENT,
  WIDGET_LAYOUT,
  iframeElements,
  isFirstInCollection,
  selectPlayer,
} from '../../pages/live_chat/constants'
import { WidgetContext } from '../../pages/live_chat/widget_page_router'
import { getSelectedWelcomeMessage } from '../../reduxes/slices/welcome_messages/selectors'
import { ProgressBar } from './progress_bar'
import { useCarouselPreviewManager } from './use_carousel_preview_manager'

export const WelcomeVideo = () => {
  const welcomeMessage = useSelector(getSelectedWelcomeMessage)
  const {
    setWelcomeVideoPlayed,
    widgetLayout,
    toggleLayout,
    setWidgetLayout,
    positionInCollection,
    collectionReady,
    collectionMuted,
  } = useContext(WidgetContext)
  const previousWidgetLayout = usePreviousProps(widgetLayout)
  const [isPreviewingInCarousel, setIsPreviewingInCarousel] = useState(false)
  const desktop = useMediaQuery(MEDIA_QUERIES.md, window.parent)

  const mediaRef = useRef<HTMLVideoElement>(null)
  const { mediaState, canPlay, muted, toggleMuted, play, pause } =
    useMediaControls({
      mediaRef,
      src: welcomeMessage?.welcome_video,
      initialAttributes: { muted: true },
    })
  useCarouselPreviewManager()

  // used to determine if the widget is visible in the viewport
  useEffect(() => {
    if (type === 'carousel' && widgetLayout === WIDGET_LAYOUT.EMBEDDED) {
      return
    }
    const onIntersectionChange = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        if (
          !collection ||
          positionInCollection === 0 ||
          isPreviewingInCarousel
        ) {
          play({
            currentTime: 0,
            muted: collection
              ? collectionMuted
              : widgetLayout !== WIDGET_LAYOUT.FULLSCREEN,
          })
        } else {
          pause()
          if (mediaRef.current) {
            mediaRef.current.currentTime = 0
          }
        }
      } else {
        if (mediaRef.current) {
          mediaRef.current.muted = true
        }
        pause()
      }
    }
    const io = new IntersectionObserver(onIntersectionChange, {
      root: window.parent.document,
    })
    if (window.frameElement) {
      io.observe(window.frameElement)
    }

    return () => {
      io.disconnect()
    }
    // do not add play or pause to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    welcomeMessage.welcome_video,
    isPreviewingInCarousel,
    positionInCollection,
    widgetLayout,
    collectionMuted,
  ])

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (
          !collection ||
          positionInCollection === 0 ||
          isPreviewingInCarousel
        ) {
          play({
            currentTime: 0,
            muted: collection
              ? collectionMuted
              : widgetLayout !== WIDGET_LAYOUT.FULLSCREEN,
          })
        }
      } else {
        if (mediaRef.current) {
          mediaRef.current.muted = true
        }
        pause()
      }
    }

    window.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [
    collectionMuted,
    isPreviewingInCarousel,
    pause,
    play,
    positionInCollection,
    widgetLayout,
  ])

  useEffect(() => {
    if (!welcomeMessage) {
      return
    }
    if (mediaState === 'playing') {
      const title = welcomeMessage.title || welcomeMessage.sgid

      if (muted) {
        ahoy.track('Video Previewed', {
          account: welcomeMessage.account_sgid,
          video: welcomeMessage.sgid,
          collection: window.speakeasySettings?.collection,
        })
      } else {
        ahoy.track('Video Played', {
          account: welcomeMessage.account_sgid,
          video: welcomeMessage.sgid,
          collection: window.speakeasySettings?.collection,
        })
      }
      // eslint-disable-next-line
      // @ts-ignore
      if (window.fbq) {
        // eslint-disable-next-line
        // @ts-ignore
        window.fbq('trackCustom', 'videoPlayed', {
          title: title,
        })
      }
      // eslint-disable-next-line
      // @ts-ignore
      if (window.gtag) {
        // eslint-disable-next-line
        // @ts-ignore
        window.gtag('event', 'videoPlayed', {
          title: title,
        })
      }
      setWelcomeVideoPlayed(true)
    }
  }, [mediaState, setWelcomeVideoPlayed, welcomeMessage, muted])

  useEffect(() => {
    if (!collection) {
      if (widgetLayout === WIDGET_LAYOUT.FULLSCREEN) {
        if (muted || type === 'widget') {
          play({ currentTime: 0, muted: false })
        }
      } else {
        if (mediaRef.current) {
          mediaRef.current.muted = true
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetLayout])

  useEffect(() => {
    if (type === 'carousel' && widgetLayout !== WIDGET_LAYOUT.FULLSCREEN) {
      const handlePlaybackChange = (event: any) => {
        if (event.detail) {
          setIsPreviewingInCarousel((prevState) => {
            if (!prevState) {
              play({ currentTime: 0, muted: collectionMuted })
            }
            return true
          })
        } else {
          setIsPreviewingInCarousel(false)
          pause()
          if (mediaRef.current) {
            mediaRef.current.currentTime = 0
          }
        }
      }
      window.addEventListener(
        PREVIEWING_STATE_CHANGE_EVENT,
        handlePlaybackChange,
      )

      return () => {
        window.removeEventListener(
          PREVIEWING_STATE_CHANGE_EVENT,
          handlePlaybackChange,
        )
      }
    }
  }, [collectionMuted, pause, play, widgetLayout])

  useEffect(() => {
    if (type !== 'carousel') {
      return
    }

    if (widgetLayout === WIDGET_LAYOUT.FULLSCREEN) {
      setIsPreviewingInCarousel(false)
    }
  }, [widgetLayout])

  useEffect(() => {
    const mediaElement = mediaRef.current
    if (mediaElement) {
      if (collection && (autoplay || isPreviewingInCarousel)) {
        mediaElement.loop = false
        if (isPreviewingInCarousel || positionInCollection === 0) {
          const onEnded = () => {
            if (
              type === 'carousel' &&
              widgetLayout !== WIDGET_LAYOUT.FULLSCREEN
            ) {
              setIsPreviewingInCarousel(false)
              const nextSibling = window.frameElement.parentElement.nextSibling
              const next =
                nextSibling && nextSibling.hasAttribute('data-widget')
                  ? nextSibling.getElementsByTagName('iframe')[0]
                  : iframeElements[0]
              next.contentWindow.dispatchEvent(
                new CustomEvent(PREVIEWING_STATE_CHANGE_EVENT, {
                  detail: true,
                }),
              )
            } else {
              selectPlayer(1)
            }
          }
          mediaElement.addEventListener('ended', onEnded)

          return () => {
            mediaElement.removeEventListener('ended', onEnded)
          }
        }
      } else {
        mediaElement.loop = true
      }
    }
  }, [isPreviewingInCarousel, positionInCollection, widgetLayout])

  useEffect(() => {
    if (
      collection &&
      type === 'carousel' &&
      previousWidgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
      widgetLayout !== WIDGET_LAYOUT.FULLSCREEN
    ) {
      pause()
      if (mediaRef.current) {
        mediaRef.current.currentTime = 0
      }
    }
  }, [isPreviewingInCarousel, pause, previousWidgetLayout, widgetLayout])

  const dispatchCollectionMutedChange = useCallback((m) => {
    iframeElements.forEach((iframe) => {
      iframe.contentWindow.dispatchEvent(
        new CustomEvent(COLLECTION_MUTED_EVENT, {
          detail: m,
        }),
      )
    })
  }, [])

  const handleToggleMuted = useCallback(() => {
    toggleMuted()
    if (collection) {
      dispatchCollectionMutedChange(mediaRef.current?.muted)
    }
  }, [dispatchCollectionMutedChange, toggleMuted])

  useEffect(() => {
    if (!collection || !isFirstInCollection) {
      return
    }

    if (
      previousWidgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
      widgetLayout !== WIDGET_LAYOUT.FULLSCREEN
    ) {
      dispatchCollectionMutedChange(true)
    } else if (
      previousWidgetLayout !== WIDGET_LAYOUT.FULLSCREEN &&
      widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
      !fullscreen
    ) {
      dispatchCollectionMutedChange(false)
    }
  }, [dispatchCollectionMutedChange, previousWidgetLayout, widgetLayout])

  const onClick = () => {
    if (collection && !collectionReady) {
      return
    }

    if (widgetLayout === WIDGET_LAYOUT.FLOATING_CLOSED) {
      setWidgetLayout(WIDGET_LAYOUT.FLOATING)
    } else {
      if (widgetLayout === WIDGET_LAYOUT.FULLSCREEN) {
        if (positionInCollection === 0) {
          handleToggleMuted()
        } else {
          selectPlayer(0)
        }
      } else {
        toggleLayout(WIDGET_LAYOUT.FULLSCREEN)
      }
    }
  }

  // preview video on hover
  useEffect(() => {
    const mediaElement = mediaRef.current
    if (
      mediaElement &&
      type === 'carousel' &&
      widgetLayout === WIDGET_LAYOUT.EMBEDDED
    ) {
      let timer: any
      const onMouseEnter = () => {
        timer = setTimeout(() => {
          iframeElements.forEach((iframe) => {
            iframe.contentWindow.dispatchEvent(
              new CustomEvent(PREVIEWING_STATE_CHANGE_EVENT, {
                detail: iframe === window.frameElement,
              }),
            )
          })
        }, 500)
      }
      const onMouseLeave = () => {
        clearTimeout(timer)
      }
      mediaElement.addEventListener('mouseenter', onMouseEnter)
      mediaElement.addEventListener('mouseleave', onMouseLeave)

      return () => {
        mediaElement.removeEventListener('mouseenter', onMouseEnter)
        mediaElement.removeEventListener('mouseleave', onMouseLeave)
      }
    }
  }, [widgetLayout])

  useEffect(() => {
    if (
      collection &&
      widgetLayout === WIDGET_LAYOUT.FLOATING &&
      isFirstInCollection
    ) {
      play()
    }
  }, [play, widgetLayout])

  const showMinimiseBtn =
    !fullscreen &&
    (type === 'widget' || widgetLayout === WIDGET_LAYOUT.FULLSCREEN)

  return welcomeMessage ? (
    <>
      {type === 'contact_form' && (
        <img
          key={`${welcomeMessage.welcome_video_poster}_placeholder_image`} // key used to get a new img element when src changes
          src={welcomeMessage.welcome_video_poster}
          className={contactFormImageStyles()}
          aria-hidden
          draggable={false}
        />
      )}
      {welcomeMessage.mime === 'video/mp4' ? (
        <>
          <img
            key={welcomeMessage.welcome_video_poster} // key used to get a new img element when src changes
            src={welcomeMessage.welcome_video_poster}
            className={videoStyles({
              hasClickHandler: !fullscreen,
              fill: !desktop && widgetLayout === WIDGET_LAYOUT.FULLSCREEN,
            })}
            draggable={false}
          />
          <figure>
            <video
              key={welcomeMessage.welcome_video} // key used to get a new video element when src changes
              ref={mediaRef}
              className={videoStyles({
                hasClickHandler: !fullscreen,
                fill: !desktop && widgetLayout === WIDGET_LAYOUT.FULLSCREEN,
              })}
              src={welcomeMessage.welcome_video}
              poster={welcomeMessage.welcome_video_poster}
              onClick={onClick}
              playsInline
              preload="none"
              disablePictureInPicture
            />
            {mediaState === 'playing' && (
              <figcaption>
                <ProgressBar mediaRef={mediaRef} />
              </figcaption>
            )}
          </figure>
        </>
      ) : (
        welcomeMessage.welcome_video && (
          <img
            key={welcomeMessage.welcome_video} // key used to get a new img element when src changes
            src={welcomeMessage.welcome_video}
            className={videoStyles({
              hasClickHandler: !fullscreen,
              fill: !desktop && widgetLayout === WIDGET_LAYOUT.FULLSCREEN,
            })}
            onClick={onClick}
            draggable={false}
          />
        )
      )}
      {(!collection ||
        widgetLayout !== WIDGET_LAYOUT.FULLSCREEN ||
        positionInCollection === 0) && (
        <Stack
          gap={2}
          css={{
            position: 'absolute',
            top: '$3',
            right: '$3',
            height: '16%',
            minHeight: '25px',
            maxHeight: '85px',
            alignItems: 'center',

            '>*': {
              height: '50%',
              aspectRatio: '1 / 1',
            },
          }}
        >
          {showMinimiseBtn && (
            <button
              onClick={() =>
                toggleLayout(
                  widgetLayout === WIDGET_LAYOUT.FULLSCREEN
                    ? WIDGET_LAYOUT.FULLSCREEN
                    : WIDGET_LAYOUT.WIDGET_MINIMISED,
                )
              }
              className={playButtonStyles()}
            >
              <MinimiseIcon title={'Close'} />
            </button>
          )}
          {widgetLayout !== WIDGET_LAYOUT.FLOATING &&
            (canPlay ? (
              mediaState === 'playing' && (
                <button
                  onClick={handleToggleMuted}
                  className={playButtonStyles()}
                >
                  {muted ? <SoundOffIcon /> : <SoundOnIcon />}
                </button>
              )
            ) : welcomeMessage.mime === 'video/mp4' ? (
              <div>
                <LoadingSpinner className={cn('fa-spin', spinnerStyles())} />
              </div>
            ) : null)}
          {!showMinimiseBtn && (
            <div /> // occupy space
          )}
        </Stack>
      )}
    </>
  ) : null
}

// eslint-disable-next-line
// @ts-ignore
const { type, fullscreen, collection, autoplay } =
  window.speakeasySettings || {}

export const PREVIEWING_STATE_CHANGE_EVENT = 'PREVIEWING_STATE_CHANGE'

const videoStyles = css({
  position: 'absolute',
  height: '100%',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'auto !important',

  variants: {
    hasClickHandler: {
      true: {
        cursor: 'pointer',
      },
    },
    fill: {
      true: {
        width: '100%',
        objectFit: 'cover',
      },
    },
  },
})

const contactFormImageStyles = css({
  width: '100%',
  height: '100%',
  position: 'absolute !important',
  objectFit: 'cover',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
})

const playButtonStyles = css({
  borderRadius: '50%',
  background: 'rgb(255,255,255,0.7)',
  border: 'none',
  cursor: 'pointer',
  pointerEvents: 'auto !important',
  padding: 0,

  svg: {
    color: 'black !important',
    width: '100%',
    height: '100%',
    padding: '$2',
  },

  '&:focus': {
    borderRadius: '50%',
  },
})

const spinnerStyles = css({
  width: '100%',
  height: '100%',
  opacity: 0.7,
  path: {
    fill: 'white',
  },
})
